// Staggered scroll reveal for a list of elements.
// Returns a Set of indexes that have entered the viewport; each index is added
// `stepMs * index` after intersection so cards cascade in. Falls back to
// revealing everything when IntersectionObserver is unavailable.
//
//   const itemRefs = useRef([]);
//   const revealed = useStaggeredReveal(itemRefs, ITEMS.length, { threshold: 0.18, stepMs: 90 });
//   <li ref={(el) => (itemRefs.current[i] = el)} className={revealed.has(i) ? "is-visible" : ""} />
import { useEffect, useState } from "react";

export function useStaggeredReveal(itemRefs, count, { threshold = 0.2, stepMs = 100 } = {}) {
  const [revealed, setRevealed] = useState(() => new Set());

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setRevealed(new Set(Array.from({ length: count }, (_, i) => i)));
      return undefined;
    }
    const timeouts = [];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = itemRefs.current.indexOf(entry.target);
          const t = setTimeout(() => setRevealed((prev) => new Set(prev).add(index)), Math.max(index, 0) * stepMs);
          timeouts.push(t);
          observer.unobserve(entry.target);
        });
      },
      { threshold }
    );
    itemRefs.current.forEach((el) => el && observer.observe(el));
    return () => {
      observer.disconnect();
      timeouts.forEach(clearTimeout);
    };
  }, [itemRefs, count, threshold, stepMs]);

  return revealed;
}

// Single-element variant: true once `rootRef` has scrolled into view.
// `afterMs` (optional) yields a second flag that flips slightly later, for a
// two-stage intro -> panel reveal.
export function useRevealOnce(rootRef, { threshold = 0.16, afterMs = 0 } = {}) {
  const [visible, setVisible] = useState(false);
  const [visibleAfter, setVisibleAfter] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      setVisibleAfter(true);
      return undefined;
    }
    let timer;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        setVisible(true);
        timer = setTimeout(() => setVisibleAfter(true), afterMs);
        observer.disconnect();
      },
      { threshold }
    );
    observer.observe(root);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [rootRef, threshold, afterMs]);

  return [visible, visibleAfter];
}
